import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, OnChanges, ChangeDetectorRef, ElementRef, HostListener } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { IFilterConfig } from '../../../../models/filter/filter-config.model';
import { IFilterValue } from '../../../../models/filter/filter-value.model';
import { IFilterOption } from '../../../../models/filter/filter-option.model';
import { FilterOperator } from '../../../../models/filter/filter-operator.enum';
import { FILTER_OPERATOR_LABELS, FILTER_OPERATOR_SYMBOLS } from '../../../../models/filter/filter-operator.enum';

@Component({
  selector: 'app-base-filter',
  standalone: false,
  templateUrl: './base-filter.component.html',
  styleUrls: ['./base-filter.component.css'],
})
export class BaseFilterComponent implements OnInit, OnDestroy, OnChanges {
  @Input() config!: IFilterConfig;
  @Input() value?: IFilterValue;
  @Input() isActive: boolean = false;
  @Input() isCompact: boolean = false;
  @Input() isCurrentlyOpen: boolean = false;
  @Input() isFromMoreFilters: boolean = false;

  @Output() filterChange = new EventEmitter<IFilterValue>();
  @Output() filterRemove = new EventEmitter<string>();
  @Output() filterActivate = new EventEmitter<string>();
  @Output() filterDropdownToggle = new EventEmitter<string>();

  public operatorControl = new FormControl<FilterOperator>(FilterOperator.EQUALS);
  public searchControl = new FormControl<string>('');
  public valueControl = new FormControl<any>(null);

  public filteredOptions: IFilterOption[] = [];
  public selectedOptions: IFilterOption[] = [];
  public isDropdownOpen = false;
  public dateModel: any = null;
  public isOperatorMenuOpen = false;

  public readonly operatorLabels = FILTER_OPERATOR_LABELS;
  public readonly operatorSymbols = FILTER_OPERATOR_SYMBOLS;
  public readonly availableOperators: FilterOperator[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
  ) {}

  ngOnInit(): void {
    if (!this.config) {
      return;
    }
    this.initializeComponent();
    this.setupSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeComponent(): void {
    this.availableOperators.push(...this.config.operators);

    // For calendar date filters (dueDate, startDate), always use EQUALS operator for range selection
    if (this.isCalendarDateFilter()) {
      this.operatorControl.setValue(FilterOperator.EQUALS);
    } else {
      this.operatorControl.setValue(this.config.defaultOperator);
    }

    if (this.value) {
      // Still respect EQUALS for calendar date filters even if value has different operator
      if (this.isCalendarDateFilter()) {
        this.operatorControl.setValue(FilterOperator.EQUALS);
      } else {
        this.operatorControl.setValue(this.value.operator);
      }
      this.valueControl.setValue(this.value.value);
      this.updateSelectedOptions();
    }

    this.filteredOptions = [...(this.config.options || [])];
  }

  ngOnChanges(changes: any): void {
    if (!changes) {
      return;
    }
    // React to config changes (especially options being populated)
    if (changes.config && !changes.config.firstChange) {
      this.filteredOptions = [...(this.config.options || [])];
      // Re-match selected options when config options are updated
      if (this.valueControl.value) {
        this.updateSelectedOptions();
      }
    }

    // React to value changes (e.g., when filters are cleared)
    if (changes.value && !changes.value.firstChange) {
      const newValue = changes.value.currentValue;
      if (!newValue || newValue.value === null || newValue.value === undefined || (Array.isArray(newValue.value) && newValue.value.length === 0)) {
        // Clear the filter UI when value is cleared
        this.valueControl.setValue(this.config.multiple ? [] : null, { emitEvent: false });
        this.selectedOptions = [];
        this.dateModel = null;
        this.searchControl.setValue('', { emitEvent: false });
        this.filteredOptions = [...(this.config.options || [])];
      } else {
        // Update the filter UI with new value
        this.operatorControl.setValue(newValue.operator, { emitEvent: false });
        this.valueControl.setValue(newValue.value, { emitEvent: false });
        this.updateSelectedOptions();
      }
    }

    // Close dropdown if another filter is opened
    if (changes.isCurrentlyOpen && !changes.isCurrentlyOpen.firstChange) {
      this.isDropdownOpen = this.isCurrentlyOpen;
      if (!this.isDropdownOpen) {
        this.isOperatorMenuOpen = false;
      }
    }
  }

  private setupSubscriptions(): void {
    // Search functionality
    this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((searchTerm) => {
      this.filterOptions(searchTerm || '');
    });

    // Operator changes
    this.operatorControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((operator) => {
      if (operator) {
        if (operator !== FilterOperator.IS_BETWEEN && Array.isArray(this.dateModel)) {
          this.dateModel = null;
          this.valueControl.setValue(null, { emitEvent: false });
        }
        this.emitFilterChange();
      }
    });

    // Value changes
    this.valueControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.emitFilterChange();
    });
  }

  public onFilterClick(event?: MouseEvent): void {
    // Stop event propagation to prevent document click listener from closing dropdown immediately
    if (event) {
      event.stopPropagation();
    }
    this.isDropdownOpen = !this.isDropdownOpen;
    this.isOperatorMenuOpen = false;

    // Notify parent to close other filters
    if (this.isDropdownOpen) {
      this.filterDropdownToggle.emit(this.config.field);
    }
  }

  public toggleOperatorOptions(): void {
    if (!this.isDropdownOpen) {
      return;
    }
    this.isOperatorMenuOpen = !this.isOperatorMenuOpen;
  }

  public onOperatorChange(operator: FilterOperator): void {
    if (!operator) {
      return;
    }
    this.operatorControl.setValue(operator);
    this.isOperatorMenuOpen = false;
  }

  public onOptionToggle(option: IFilterOption): void {
    if (!option) {
      return;
    }
    if (this.config.multiple) {
      this.toggleMultiSelectOption(option);
    } else {
      this.selectSingleOption(option);
    }
  }

  public onDateSelected(): void {
    // For single date we store a Date; for range, an array [start, end]
    this.valueControl.setValue(this.dateModel);
    this.emitFilterChange();
  }

  public onClearDate(): void {
    this.dateModel = null;
    this.valueControl.setValue(null);
    this.emitFilterChange();
  }

  public onRemoveFilter(): void {
    // Clear all selected options and reset form controls
    this.valueControl.setValue(this.config.multiple ? [] : null);
    this.selectedOptions = [];
    this.dateModel = null;
    this.searchControl.setValue('');

    // Close dropdown
    this.isDropdownOpen = false;
    this.isOperatorMenuOpen = false;

    // Emit remove event
    this.filterRemove.emit(this.config.field); // Use field, not id
  }

  public onCloseDropdown(): void {
    this.isDropdownOpen = false;
    this.isOperatorMenuOpen = false;
  }

  public onClearSelection(): void {
    this.valueControl.setValue(this.config.multiple ? [] : null);
    this.selectedOptions = [];
    this.searchControl.setValue('');

    this.filteredOptions = [...(this.config.options || [])];

    this.isDropdownOpen = false;
    this.isOperatorMenuOpen = false;
  }

  public get hasSelectedOptions(): boolean {
    const currentValue = this.valueControl.value;

    // For multi-select, check if array has items
    if (this.config.multiple && Array.isArray(currentValue)) {
      return currentValue.length > 0;
    }

    // For single-select, check if a value is set
    return currentValue !== null && currentValue !== undefined && currentValue !== '';
  }

  private filterOptions(searchTerm: string): void {
    if (!searchTerm.trim()) {
      this.filteredOptions = [...(this.config.options || [])];
      return;
    }

    const term = searchTerm.toLowerCase();
    this.filteredOptions = (this.config.options || []).filter((option: IFilterOption) => {
      if (!option || !option.label) {
        return false;
      }
      return option.label.toLowerCase().includes(term);
    });
  }

  private toggleMultiSelectOption(option: IFilterOption): void {
    if (!option) {
      return;
    }
    // Copy rather than mutate this.valueControl.value in place — that array is the same
    // object referenced by the currently-stored active-filter value (see applyNewFilter()
    // in filter-toolbar.component.ts), so splicing/pushing it directly would corrupt the
    // "previous" snapshot WorkspaceStore compares against before the change is even emitted.
    const currentValue = [...(this.valueControl.value || [])];
    // Case-insensitive comparison for string values
    const optionIndex = currentValue.findIndex((val: any) => {
      if (typeof val === 'string' && typeof option.value === 'string') {
        return val.toLowerCase() === option.value.toLowerCase();
      }
      return val === option.value;
    });

    if (optionIndex >= 0) {
      currentValue.splice(optionIndex, 1);
    } else {
      currentValue.push(option.value);
    }

    this.valueControl.setValue([...currentValue]);
    this.updateSelectedOptions();
  }

  private selectSingleOption(option: IFilterOption): void {
    if (!option) {
      return;
    }
    this.valueControl.setValue(option.value);
    this.selectedOptions = [option];
    this.isDropdownOpen = false;
  }

  private updateSelectedOptions(): void {
    const currentValue = this.valueControl.value;
    if (!currentValue) {
      this.selectedOptions = [];
      return;
    }

    if (Array.isArray(currentValue)) {
      // Case-insensitive matching for string values
      this.selectedOptions = (this.config.options || []).filter((option: IFilterOption) =>
        currentValue.some((val: any) => {
          if (typeof val === 'string' && typeof option.value === 'string') {
            return val.toLowerCase() === option.value.toLowerCase();
          }
          return val === option.value;
        }),
      );
    } else {
      // Case-insensitive matching for string values
      const option = (this.config.options || []).find((opt: IFilterOption) => {
        if (typeof currentValue === 'string' && typeof opt.value === 'string') {
          return currentValue.toLowerCase() === opt.value.toLowerCase();
        }
        return opt.value === currentValue;
      });
      this.selectedOptions = option ? [option] : [];
    }
  }

  private emitFilterChange(): void {
    const operator = this.operatorControl.value;
    const value = this.valueControl.value;

    if (!operator) {
      return;
    }

    // Check if value is actually empty (for arrays, check length; for other types, check null/undefined/empty)
    const isEmpty = value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);

    // Always emit the filter change (even if empty) so the parent can track it
    // The parent will determine if it should be shown as active based on the value
    const filterValue: IFilterValue = {
      field: this.config.field,
      operator,
      value: isEmpty ? (this.config.multiple ? [] : null) : value,
      label: this.getDisplayLabel(),
    };

    // Activate the filter when a value is set for the first time
    if (!this.isActive && !isEmpty) {
      this.filterActivate.emit(this.config.field);
    }

    this.filterChange.emit(filterValue);
  }

  private getDisplayLabel(): string {
    const value = this.valueControl.value;
    if (value === null || value === undefined || value === '') return '';

    if (Array.isArray(value)) {
      const dates = value.filter((v) => v instanceof Date);
      if (dates.length > 0) {
        return dates.map((d) => this.formatDate(d)).join(' – ');
      }
      return value.length > 0 ? `${value.length} selected` : '';
    }

    if (value instanceof Date) {
      return this.formatDate(value);
    }

    const option = (this.config?.options || []).find((opt: IFilterOption) => opt?.value === value);
    if (option?.label) return option.label;

    return value != null ? String(value) : '';
  }

  private formatDate(date: Date | null | undefined): string {
    if (!(date instanceof Date) || isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  }

  public getDisplayText(): string {
    if (!this.isActive || !this.valueControl.value) {
      return this.config.label;
    }

    const operator = this.operatorControl.value;
    const value = this.getDisplayLabel();

    return `${this.config.label} ${this.operatorSymbols[operator!]} ${value}`;
  }

  public getOptionDisplayLabel(option: IFilterOption): string {
    if (!option || !option.label) {
      return '';
    }
    return option.label.length > 25 ? `${option.label.slice(0, 22)}...` : option.label;
  }

  public isOptionLabelTruncated(option: IFilterOption): boolean {
    return !!option?.label && option.label.length > 25;
  }

  public isOptionSelected(option: IFilterOption): boolean {
    if (!option) {
      return false;
    }
    const currentValue = this.valueControl.value;
    if (!currentValue) return false;

    if (Array.isArray(currentValue)) {
      // Case-insensitive comparison for string values
      return currentValue.some((val: any) => {
        if (typeof val === 'string' && typeof option.value === 'string') {
          return val.toLowerCase() === option.value.toLowerCase();
        }
        return val === option.value;
      });
    }

    // Case-insensitive comparison for string values
    if (typeof currentValue === 'string' && typeof option.value === 'string') {
      return currentValue.toLowerCase() === option.value.toLowerCase();
    }
    return currentValue === option.value;
  }

  public trackByOptionId(index: number, option: IFilterOption): string {
    if (!option) {
      return '';
    }
    return option.id;
  }

  // Check if this is a calendar-based date filter (dueDate or startDate)
  public isCalendarDateFilter(): boolean {
    return this.config && (this.config.field === 'dueDate' || this.config.field === 'startDate');
  }

  // Range mode only when IS_BETWEEN operator is selected
  public isRangeDateOperator(): boolean {
    return (this.operatorControl.value ?? null) === FilterOperator.IS_BETWEEN;
  }

  // Click outside to close dropdown (Jira behavior)
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!event) {
      return;
    }
    if (!this.isDropdownOpen) return;

    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.onCloseDropdown();
      this.cdr.detectChanges();
    }
  }
}
